import { ReactElement } from 'react'

import { Button } from 'antd'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAppConfig } from 'appConfig'
import { TextButton } from 'components/TextButton'
import { signOut } from 'features/Auth/authService'
import { DatadogService } from 'features/Monitoring/Datadog'
import { RumViewTracker } from 'features/Monitoring/Datadog/RumViewTracker'
import { userHasAssumeAnyUserPermissions } from 'helpers/featurePermissions'
import { useAuthDomainValue } from 'hooks'
import CobaltJSLogo from 'images/logo/CobaltJSLogo'
import { sanitizeLocationState } from 'routing/locationState'
import { UserPermission } from 'types'

import * as authStyles from './styles.scss'

const AuthLayout = (props: Record<string, unknown>): ReactElement => {
    const location = useLocation()
    const signedIn = useAuthDomainValue<boolean>('signedIn')
    const organizationId = useAuthDomainValue('organizationId')
    const userFeaturePermissions: UserPermission[] = useAuthDomainValue(
        'userFeaturePermissions'
    )
    const { t, i18n } = useTranslation('common')

    const { isChinaBuild } = useAppConfig()

    const { from } = sanitizeLocationState(location.state)

    const isChinese = i18n.language === 'zh'

    const changeLanguage = (): void => {
        i18n.changeLanguage(isChinese ? 'en' : 'zh')
    }

    const changeLanguageText = isChinese
        ? 'Switch Language to English'
        : t('common:languageSelect.zh', 'Switch Language to Chinese')

    if (
        signedIn &&
        (organizationId ||
            userHasAssumeAnyUserPermissions(userFeaturePermissions))
    ) {
        return <Navigate to={from} />
    }

    return (
        <div className={authStyles.container}>
            <RumViewTracker
                service={DatadogService.AUTH}
                name={location.pathname}
            />
            {signedIn && (
                <Button
                    className={authStyles.logout}
                    type="link"
                    onClick={() => signOut()}
                >
                    {t('common:logOut', 'Log Out')}
                </Button>
            )}
            <div className={authStyles.logo}>
                <CobaltJSLogo className={authStyles.authLogo} />
            </div>
            <div
                className={classNames(
                    authStyles['form-container'],
                    authStyles.form
                )}
            >
                <Outlet {...props} />
            </div>
            {(isChinaBuild || isChinese) && (
                <div className={authStyles.languageSelector}>
                    <TextButton onClick={changeLanguage}>
                        {changeLanguageText}
                    </TextButton>
                </div>
            )}
        </div>
    )
}

export default AuthLayout
